import { useState, useEffect } from 'react'
import { useTranslation }      from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { listQuestions, deleteQuestion, copyQuestion, listQuestionBanks } from '@/api/resources'
import {
  Button, PageHeader, Badge, EmptyState, Spinner, FIELD_CLS, FIELD_AUTO_CLS, SearchInput,
  SortTh, TableFooter,
} from '@/components/ui/primitives'
import usePaginationSort from '@/hooks/usePaginationSort'
import CreateQuestion from '@/components/questions/CreateQuestion'
import { Plus, Pencil, Trash2, Eye, X, Copy, HelpCircle } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'
import { fetchAllPages } from '@/utils/fetchAll'
import { sanitizeHtml } from '@/utils/sanitize'

const TYPE_COLORS = {
  1: 'blue', 2: 'purple', 3: 'amber',
  4: 'green', 5: 'green', 6: 'red', 7: 'blue',
}

// Question content is rich HTML (may include <style>/<img> tags) — strip it
// down to plain text for compact previews (table cells, copy dialog).
function stripHtml(html) {
  return String(html ?? '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ── SlideOver panel ───────────────────────────────────────────────────
function SlideOver({ open, title, badge, onClose, children }) {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={onClose} />
      <div
        className="fixed top-0 right-0 h-full w-full max-w-2xl border-l z-50 flex flex-col shadow-xl"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold" style={{ color: 'var(--ink)' }}>{title}</h2>
            {badge}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: 'var(--ink-3)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </>
  )
}

// ── Copy Dialog ───────────────────────────────────────────────────────
function CopyDialog({ question, banks, onClose, onCopied }) {
  const { t } = useTranslation()
  const [targetBank, setTargetBank] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  async function handleCopy() {
    if (!targetBank) return
    setLoading(true); setError(null)
    try {
      await copyQuestion(question.id, { question_bank: Number(targetBank) })
      onCopied()
      onClose()
    } catch (e) {
      setError(e.response?.data?.detail || e.message)
    } finally { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
      <div className="rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between">
          <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Savolni nusxalash</h3>
          <button onClick={onClose} className="text-xl transition-colors" style={{ color: 'var(--ink-3)' }}>✕</button>
        </div>
        <p className="text-sm line-clamp-2" style={{ color: 'var(--ink-2)' }}>"{stripHtml(question.content)}"</p>
        <div>
          <label className="block text-[12.5px] font-medium mb-1.5" style={{ color: 'var(--ink-2)' }}>Qaysi bazaga nusxalansin? *</label>
          <select value={targetBank} onChange={e => setTargetBank(e.target.value)} className={FIELD_CLS}>
            <option value="">— Savollar bazasini tanlang —</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.name}{b.subject_name ? ` (${b.subject_name})` : ''}</option>)}
          </select>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel', 'Bekor')}</Button>
          <Button variant="primary" onClick={handleCopy} disabled={!targetBank || loading}>
            {loading ? 'Nusxalanmoqda...' : 'Nusxalash'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Render correct-answer in human-readable form ──────────────────────
function renderCorrectAnswer(question) {
  const { question_type: type, correct_answer: ca, options } = question
  if (!ca) return null

  const optText = (id) => options?.find(o => o.id === id || o.id === Number(id))?.text ?? `#${id}`
  const correctStyle = { color: 'var(--success)', fontWeight: 600, fontSize: 14 }
  const mutedStyle   = { color: 'var(--ink-3)', fontSize: 12 }

  switch (type) {
    case 1:
      return <span style={correctStyle}>{optText(ca.answer)}</span>

    case 2:
      return (
        <ul className="space-y-0.5">
          {(ca.answers ?? []).map((id, i) => (
            <li key={i} className="flex items-center gap-1.5" style={correctStyle}>
              <span style={{ color: 'var(--mint)' }}>✓</span>{optText(id)}
            </li>
          ))}
        </ul>
      )

    case 3:
      return <span style={correctStyle}>{ca.answer ? 'Ha (True)' : "Yo'q (False)"}</span>

    case 4:
    case 5:
      return <span className="italic" style={mutedStyle}>AI tomonidan baholanadi</span>

    case 6:
      return (
        <ul className="space-y-0.5">
          {(ca.answers ?? []).map((ans, i) => (
            <li key={i} className="flex items-center gap-1.5" style={correctStyle}>
              <span style={mutedStyle}>{i + 1}.</span>{ans}
            </li>
          ))}
        </ul>
      )

    case 7:
      return (
        <ul className="space-y-1">
          {(ca.pairs ?? []).map((pair, i) => {
            const left  = options?.find(o => o.left?.id  === pair.left  || o.left?.id  === Number(pair.left))?.left?.text  ?? `#${pair.left}`
            const right = options?.find(o => o.right?.id === pair.right || o.right?.id === Number(pair.right))?.right?.text ?? `#${pair.right}`
            return (
              <li key={i} className="text-sm flex items-center gap-2">
                <span style={{ color: 'var(--ink-2)' }}>{left}</span>
                <span style={mutedStyle}>→</span>
                <span style={correctStyle}>{right}</span>
              </li>
            )
          })}
        </ul>
      )

    default:
      return null
  }
}

// ── Question View (read-only) ─────────────────────────────────────────
function QuestionView({ question, t, onClose }) {
  const type    = question.question_type
  const ca      = question.correct_answer ?? {}
  const options = question.options ?? []

  const correctIds = new Set(
    type === 1 ? [ca.answer] :
    type === 2 ? (ca.answers ?? []) :
    []
  )

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Badge variant={TYPE_COLORS[type] ?? 'gray'}>{t(`questions.types.${type}`)}</Badge>
        {question.question_bank_name && (
          <span className="text-xs font-semibold" style={{ color: 'var(--lav)' }}>📚 {question.question_bank_name}</span>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--ink-3)' }}>{t('questions.content')}</p>
        {question.content
          ? <div className="text-sm leading-relaxed" style={{ color: 'var(--ink)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(question.content) }} />
          : <p className="text-sm" style={{ color: 'var(--ink)' }}>—</p>}
      </div>

      {options.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-3)' }}>Variantlar</p>
          <ul className="space-y-1.5">
            {options.map((o, i) => {
              const id      = o.id ?? (i + 1)
              const isRight = correctIds.has(id) || correctIds.has(String(id))
              return (
                <li
                  key={i}
                  className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm border"
                  style={isRight
                    ? { background: 'var(--mint-tint)', borderColor: 'var(--mint-soft)', color: 'var(--success)', fontWeight: 600 }
                    : { background: 'var(--surface-2)', borderColor: 'var(--border)', color: 'var(--ink-2)' }
                  }
                >
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={isRight
                      ? { background: 'var(--mint)', color: '#fff' }
                      : { background: 'var(--bg-soft)', color: 'var(--ink-3)' }
                    }
                  >
                    {isRight ? '✓' : id}
                  </span>
                  {o.text
                    ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(o.text) }} />
                    : '—'}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {(type === 3 || type === 4 || type === 5 || type === 6 || type === 7) && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--ink-3)' }}>To'g'ri javob</p>
          <div className="rounded-xl px-3 py-2.5 border" style={{ background: 'var(--mint-tint)', borderColor: 'var(--mint-soft)' }}>
            {renderCorrectAnswer(question)}
          </div>
        </div>
      )}

      {question.topic_name && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--ink-3)' }}>{t('nav.topics', 'Mavzu')}</p>
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>{question.topic_name}</p>
        </div>
      )}

      <div className="flex justify-end pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <Button variant="secondary" onClick={onClose}>{t('common.close', 'Yopish')}</Button>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────
export default function QuestionsPage() {
  const { t }                         = useTranslation()
  const [searchParams]                = useSearchParams()
  const navigate                      = useNavigate()
  const [questions, setQuestions]     = useState([])
  const [banks,     setBanks]         = useState([])
  const [loading,   setLoading]       = useState(true)
  const [error,     setError]         = useState(null)
  const [panel,     setPanel]         = useState(null)
  const [copyTarget, setCopyTarget]   = useState(null) // question to copy
  const [confirm,   setConfirm]       = useState(null)
  const [search,    setSearch]        = useState('')
  const [typeFilter,setTypeFilter]    = useState('')
  const [bankFilter,setBankFilter]    = useState(() => searchParams.get('bank') || '')
  const examBankIds  = searchParams.get('banks') // comma-separated ids, set when arriving from an exam's "Savollar" link
  const examTitle    = searchParams.get('examTitle')

  async function fetchQuestions() {
    setLoading(true)
    setError(null)
    const baseParams = {}
    if (examBankIds) baseParams.question_bank_in = examBankIds
    else if (bankFilter) baseParams.question_bank = bankFilter
    try {
      setQuestions(await fetchAllPages(listQuestions, baseParams))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllPages(listQuestionBanks).then(setBanks).catch(() => {})
  }, [])

  useEffect(() => { fetchQuestions() }, [bankFilter, examBankIds])

  async function handleConfirmDelete() {
    await deleteQuestion(confirm)
    setConfirm(null)
    fetchQuestions()
  }

  const filtered = questions.filter(q => {
    const text = String(q.content ?? '').toLowerCase()
    const matchSearch = !search || text.includes(search.toLowerCase())
    const matchType   = !typeFilter || String(q.question_type) === typeFilter
    return matchSearch && matchType
  })

  const { paginated, page, setPage, pageSize, setPageSize, sortKey, sortDir, handleSort, totalPages, totalCount } = usePaginationSort(filtered)

  useEffect(() => { setPage(1) }, [search, typeFilter, bankFilter, examBankIds])

  const panelTitle =
    panel?.mode === 'create' ? t('questions.createNew') :
    panel?.mode === 'edit'   ? t('common.edit') :
    panel?.mode === 'view'   ? t('common.view', 'Ko\'rish') : ''

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('questions.title')}
        subtitle={`${filtered.length} ${t('common.total').toLowerCase()}`}
        actions={<Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>{t('questions.createNew')}</Button>}
      />

      {examBankIds && (
        <div className="flex items-center gap-2 rounded-xl px-4 py-2.5 border"
          style={{ background: 'var(--coral-tint)', borderColor: 'var(--coral-soft)' }}>
          <span className="text-sm font-medium" style={{ color: 'var(--coral)' }}>
            Filtr: <strong>{examTitle || 'Imtihon'}</strong> imtihoniga tegishli savollar
          </span>
          <button type="button" onClick={() => navigate('/questions')}
            className="ml-auto text-xs font-semibold underline" style={{ color: 'var(--coral)' }}>
            Filtrni tozalash
          </button>
        </div>
      )}

      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search', 'Qidirish...')}
        />
        {!examBankIds && (
          <select value={bankFilter} onChange={e => setBankFilter(e.target.value)} className={FIELD_AUTO_CLS}>
            <option value="">Barcha bazalar</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={FIELD_AUTO_CLS}>
          <option value="">{t('common.filter')}: {t('questions.questionType')}</option>
          {[1,2,3,4,5,6,7].map(qt => <option key={qt} value={qt}>{qt}. {t(`questions.types.${qt}`)}</option>)}
        </select>
      </div>

      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : error ? (
          <div className="p-6 text-red-600 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={<HelpCircle size={48} />} title={t('common.noData')} action={<Button icon={<Plus size={15} />} onClick={() => setPanel({ mode: 'create' })}>{t('questions.createNew')}</Button>} />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('questions.questionType')}</th>
                  <SortTh sortKey="content" currentKey={sortKey} currentDir={sortDir} onSort={handleSort}>{t('questions.content')}</SortTh>
                  <th>Savollar bazasi</th>
                  <SortTh sortKey="created_at" currentKey={sortKey} currentDir={sortDir} onSort={handleSort}>{t('common.createdAt')}</SortTh>
                  <th>{t('common.actions', 'Amallar')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(q => (
                  <tr key={q.id}>
                    <td>
                      <Badge variant={TYPE_COLORS[q.question_type] ?? 'gray'}>{t(`questions.types.${q.question_type}`)}</Badge>
                    </td>
                    <td className="max-w-xs"><p className="primary truncate">{stripHtml(q.content) || '—'}</p></td>
                    <td><span className="secondary">{q.question_bank_name || '—'}</span></td>
                    <td><span className="secondary">{new Date(q.created_at).toLocaleDateString()}</span></td>
                    <td className="tr">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setPanel({ mode: 'view', question: q })} className="tbl-btn tbl-go" title={t('common.view', 'Ko\'rish')}><Eye size={15} /></button>
                        <button onClick={() => setPanel({ mode: 'edit', question: q })} className="tbl-btn tbl-edit" title={t('common.edit')}><Pencil size={15} /></button>
                        <button onClick={() => setCopyTarget(q)} className="tbl-btn tbl-go" title="Nusxalash"><Copy size={15} /></button>
                        <button onClick={() => setConfirm(q.id)} className="tbl-btn tbl-danger" title={t('common.delete')}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </>
        )}
      </div>

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {copyTarget && (
        <CopyDialog
          question={copyTarget}
          banks={banks}
          onClose={() => setCopyTarget(null)}
          onCopied={fetchQuestions}
        />
      )}

      <SlideOver
        open={!!panel}
        title={panelTitle}
        badge={panel?.mode === 'view' && panel.question ? (
          <Badge variant={TYPE_COLORS[panel.question.question_type] ?? 'gray'}>
            {t(`questions.types.${panel.question.question_type}`)}
          </Badge>
        ) : null}
        onClose={() => setPanel(null)}
      >
        {panel?.mode === 'view' && panel.question && (
          <QuestionView question={panel.question} t={t} onClose={() => setPanel(null)} />
        )}
        {(panel?.mode === 'create' || panel?.mode === 'edit') && (
          <CreateQuestion
            key={panel?.question?.id ?? 'new'}
            initial={panel?.mode === 'edit' ? panel.question : null}
            onSaved={() => { setPanel(null); fetchQuestions() }}
          />
        )}
      </SlideOver>
    </div>
  )
}

